import React, { useRef, useState } from "react";

// Accepted MIME types for outgoing media (matching backend ALLOWED_OUTGOING_MIME)
const ACCEPTED_MEDIA_TYPES =
  "video/mp4,video/3gpp,audio/aac,audio/mp4,audio/mpeg,audio/amr,audio/ogg,image/jpeg,image/png,image/webp,application/pdf";

const MAX_FILE_BYTES = 16 * 1024 * 1024; // 16 MB

function getMediaIcon(mimeType) {
  if (!mimeType) return "bi-paperclip";
  if (mimeType.startsWith("video/")) return "bi-camera-video-fill";
  if (mimeType.startsWith("audio/")) return "bi-mic-fill";
  if (mimeType.startsWith("image/")) return "bi-image-fill";
  if (mimeType === "application/pdf") return "bi-file-earmark-pdf-fill";
  return "bi-file-earmark-fill";
}

function formatBytes(bytes) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export default function MessageComposer({
  replyText,
  setReplyText,
  sending,
  sessionWindow = { active: false, hoursLeft: 0 },
  inputRef,
  onSendReply,
  onInsertQuickReply,
  onOpenTemplateModal,
  onSendMedia, // (file, caption) => Promise<void>
  replyToMsg = null,
  onCancelReply,
}) {
  const fileInputRef = useRef(null);
  const [attachedFile, setAttachedFile] = useState(null);
  const [fileError, setFileError] = useState("");
  const [mediaCaption, setMediaCaption] = useState("");

  const handleKeyDown = (e) => {
    if (e.key === "Escape") {
      if (replyToMsg && onCancelReply) {
        onCancelReply();
      }
    } else if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      onSendReply(e);
    }
  };

  const handlePaperclipClick = () => {
    setFileError("");
    fileInputRef.current?.click();
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // reset so same file can be re-selected
    if (!file) return;

    if (file.size > MAX_FILE_BYTES) {
      setFileError(`File too large: ${formatBytes(file.size)}. Maximum is 16 MB.`);
      return;
    }

    setFileError("");
    setAttachedFile(file);
    setMediaCaption("");
  };

  const handleClearAttachment = () => {
    setAttachedFile(null);
    setFileError("");
    setMediaCaption("");
  };

  const handleSendMedia = async (e) => {
    e?.preventDefault();
    if (!attachedFile || sending) return;
    if (typeof onSendMedia === "function") {
      await onSendMedia(attachedFile, mediaCaption);
      setAttachedFile(null);
      setMediaCaption("");
    }
  };

  return (
    <footer className="wa-composer-container" aria-label="Message Composer">
      {/* 1. Slim Session Notice (If Window Expired) */}
      {!sessionWindow.active && (
        <div className="wa-session-notice-strip">
          <i className="bi bi-info-circle-fill text-warning me-2"></i>
          <span className="wa-notice-text">
            <strong>24h Window Closed.</strong> Meta requires an approved template to restart the chat:
          </span>
          <button
            type="button"
            className="wa-btn-choose-template-slim"
            onClick={onOpenTemplateModal}
          >
            <i className="bi bi-lightning-charge-fill me-1"></i> Choose Template
          </button>
        </div>
      )}

      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        style={{ display: "none" }}
        accept={ACCEPTED_MEDIA_TYPES}
        onChange={handleFileChange}
        tabIndex={-1}
        aria-hidden="true"
      />

      {/* File validation error banner */}
      {fileError && (
        <div className="v-media-file-error" role="alert">
          <i className="bi bi-exclamation-triangle-fill me-1"></i>
          {fileError}
        </div>
      )}

      {/* Media Attachment Preview Bar (shown when file is selected) */}
      {attachedFile && (
        <div className="v-media-attach-preview">
          <div className="v-media-attach-info">
            <i className={`bi ${getMediaIcon(attachedFile.type)} fs-5 me-1 text-primary`}></i>
            <span className="v-media-attach-name">{attachedFile.name}</span>
            <span className="v-media-attach-size">({formatBytes(attachedFile.size)})</span>
          </div>

          {/* Optional Caption for videos / images / docs */}
          {!attachedFile.type.startsWith("audio/") && (
            <input
              type="text"
              className="v-media-caption-input"
              placeholder="Add an optional caption..."
              value={mediaCaption}
              onChange={(e) => setMediaCaption(e.target.value)}
              disabled={sending}
              maxLength={1024}
            />
          )}

          <div className="v-media-attach-actions">
            <button
              type="button"
              className="v-btn-attach-cancel"
              onClick={handleClearAttachment}
              disabled={sending}
              title="Remove attachment"
            >
              <i className="bi bi-x-lg"></i> Cancel
            </button>
            <button
              type="button"
              className="v-btn-send-media"
              onClick={handleSendMedia}
              disabled={sending}
              title="Send media file via WhatsApp"
            >
              {sending ? (
                <>
                  <span className="spinner-border spinner-border-sm me-1" role="status"></span>
                  Sending...
                </>
              ) : (
                <>
                  <i className="bi bi-send-fill me-1"></i> Send Attachment
                </>
              )}
            </button>
          </div>
        </div>
      )}

      {/* 2. Fast Quick Reply Chips (Single Horizontal Scrollable Line) */}
      <div className="wa-quick-replies-scroll">
        <span className="wa-quick-tag">
          <i className="bi bi-lightning me-1 text-warning"></i> Quick:
        </span>
        <button
          type="button"
          className="wa-chip"
          onClick={() =>
            onInsertQuickReply(
              "🙏 Namaskaram! How can we assist you regarding your VaithikaSri subscription today?"
            )
          }
        >
          🙏 Namaskaram Greeting
        </button>
        <button
          type="button"
          className="wa-chip"
          onClick={() =>
            onInsertQuickReply(
              "Your subscription has been verified and active. Your next monthly magazine issue will be dispatched soon via post."
            )
          }
        >
          📦 Magazine Dispatch
        </button>
        <button
          type="button"
          className="wa-chip"
          onClick={() =>
            onInsertQuickReply(
              "Thank you for reaching out to Sri VaithikaSri Publications. For online renewals, visit https://vaithikasri.com"
            )
          }
        >
          ✨ Thank You & Link
        </button>
        <button
          type="button"
          className="wa-chip"
          onClick={() =>
            onInsertQuickReply(
              "Kindly share your registered postal address or receipt number so our dispatch team can trace your shipment."
            )
          }
        >
          🔍 Address Verification
        </button>
        <button
          type="button"
          className="wa-chip"
          onClick={() =>
            onInsertQuickReply(
              "Namaskaram! Your VaithikaSri annual subscription can be renewed online via UPI/Card at https://vaithikasri.com or by contacting our office."
            )
          }
        >
          🔄 Subscription Renewal
        </button>
      </div>

      {/* 2.5. Active Quoted Reply Bar (Authentic WhatsApp Web) */}
      {replyToMsg && (
        <div className="wa-reply-quote-bar" role="region" aria-label="Quoted message">
          <div className="wa-reply-quote-accent"></div>
          <div className="wa-reply-quote-content">
            <div className="wa-reply-quote-sender">
              <i className="bi bi-reply-fill me-1"></i>
              Replying to {replyToMsg.direction === "outbound" ? "You" : replyToMsg.sender_name || "Customer"}
            </div>
            <div className="wa-reply-quote-snippet text-truncate">
              {replyToMsg.message_body || "[Media Attachment]"}
            </div>
          </div>
          <button
            type="button"
            className="wa-reply-quote-close"
            onClick={onCancelReply}
            title="Cancel quoted reply (Esc)"
            aria-label="Cancel reply"
          >
            <i className="bi bi-x-lg"></i>
          </button>
        </div>
      )}

      {/* 3. Main Input Bar (WhatsApp Web Style) */}
      <form
        className={`wa-input-row ${replyToMsg ? "is-replying" : ""}`}
        onSubmit={onSendReply}
      >
        {/* Left Action Buttons */}
        <div className="wa-input-left-tools">
          <button
            type="button"
            className="wa-tool-btn"
            title="Insert Greeting"
            onClick={() => onInsertQuickReply("🙏 Namaskaram! ")}
          >
            <i className="bi bi-emoji-smile"></i>
          </button>
          <button
            type="button"
            className="wa-tool-btn template"
            title="Send Approved Meta Template"
            onClick={onOpenTemplateModal}
          >
            <i className="bi bi-lightning-charge"></i>
          </button>
          <button
            type="button"
            className="wa-tool-btn paperclip"
            title="Attach Video, Audio, or Document"
            onClick={handlePaperclipClick}
            aria-label="Attach media file"
          >
            <i className="bi bi-paperclip"></i>
          </button>
        </div>

        {/* Input Textarea */}
        <div className="wa-input-box-wrapper">
          <textarea
            ref={inputRef}
            className="wa-input-field"
            rows={1}
            placeholder={
              replyToMsg
                ? "Type your reply... (Enter to send, Shift+Enter for newline)"
                : sessionWindow.active
                ? "Type a reply... (Enter to send, Shift+Enter for newline)"
                : "Type a message or ⚡ template..."
            }
            value={replyText}
            onChange={(e) => setReplyText(e.target.value)}
            onKeyDown={handleKeyDown}
            disabled={sending}
            aria-label="Message content"
          />
        </div>

        {/* Circular Send Button */}
        <button
          type="submit"
          className={`wa-btn-send ${replyText.trim() ? "active" : ""}`}
          disabled={!replyText.trim() || sending}
          title="Send Message"
          aria-label="Send"
        >
          {sending ? (
            <span className="spinner-border spinner-border-sm" role="status"></span>
          ) : (
            <i className="bi bi-send-fill"></i>
          )}
        </button>
      </form>
    </footer>
  );
}
